import { z } from "zod";
import {
  MeterUploadRowStatus,
  type MeterUploadRowData,
  type MeterUploadRowStatusValue,
} from "./buildTrialRunRow";

// The simple value fields that are editable directly in the trial-run grid.
// Lookup cells (subscriber, offtake) are selects; derived cells (volume, last
// read, status) are server-computed and never editable. group/seq cascade from
// the picked offtake but are also editable so an operator can override them — a
// value that disagrees with the offtake surfaces as a trial-run warning.
export const INLINE_EDITABLE_FIELDS = [
  "reading",
  "readAt",
  "serialNo",
  "group",
  "seq",
] as const;
export type InlineEditableField = (typeof INLINE_EDITABLE_FIELDS)[number];

// One zod schema per editable field. `reading` coerces the raw input string to a
// non-negative number; `readAt` is a required date-input string (yyyy-mm-dd);
// `serialNo` and `group` are optional free text, so any string — including empty —
// commits; `seq` coerces to a non-negative integer (an empty input becomes 0).
const fieldSchemas: Record<InlineEditableField, z.ZodType> = {
  reading: z.coerce.number().nonnegative(),
  readAt: z.string().min(1),
  serialNo: z.string(),
  group: z.string(),
  seq: z.coerce.number().int().nonnegative(),
};

// On a failed commit the cell surfaces an existing validation translation key so
// the message stays consistent with the modal and this module stays i18n-agnostic.
const errorKeys: Record<InlineEditableField, string> = {
  reading: "bulk_meter_upload.validation.reading",
  readAt: "bulk_meter_upload.validation.read_at",
  serialNo: "bulk_meter_upload.validation.serial_no",
  group: "bulk_meter_upload.validation.group",
  seq: "bulk_meter_upload.validation.seq",
};

export type CellCommitResult =
  | { ok: true; rowData: MeterUploadRowData }
  | { ok: false; error: string };

// A row stays inline-editable until it is ledgered (Success). Draft and Failed
// (retryable) rows remain editable; Success rows are locked read-only.
export const isRowEditable = (status: MeterUploadRowStatusValue): boolean =>
  status !== MeterUploadRowStatus.Success;

// Validate one cell's raw input and, when valid, return a new rowData with the
// parsed field applied. On invalid input it returns the field's error key and
// leaves rowData untouched — the caller must not commit (no callback, no persist).
export const commitCell = ({
  field,
  rawValue,
  rowData,
}: {
  field: InlineEditableField;
  rawValue: string;
  rowData: MeterUploadRowData;
}): CellCommitResult => {
  const parsed = fieldSchemas[field].safeParse(rawValue);
  if (!parsed.success) {
    return { ok: false, error: errorKeys[field] };
  }
  return { ok: true, rowData: { ...rowData, [field]: parsed.data } };
};
